import { useState, useEffect, useRef } from 'react';
import { 
  PaperPlaneTilt, 
  At, 
  Clock, 
  User,
  Pencil,
  Trash 
} from '@phosphor-icons/react';
import { 
  addNoteToPost, 
  getPostNotes, 
  getAllUsers, 
  createNotifications,
  updateNote,
  deleteNote 
} from '../../../services/notesService';
import { useAuth } from '../../../AuthContext';

export default function PostNotes({ postId, onClose }) {
  const { currentUser } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const textareaRef = useRef(null);

  // Load notes and users on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load notes and users separately to handle individual failures
        const notesPromise = getPostNotes(postId).catch(err => {
          console.error('Error loading notes:', err);
          return [];
        });
        
        const usersPromise = getAllUsers().catch(err => {
          console.error('Error loading users:', err);
          return [];
        });
        
        const [notesData, usersData] = await Promise.all([notesPromise, usersPromise]);
        setNotes(notesData);
        setAllUsers(usersData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [postId]);

  // Handle text input changes and detect @ mentions
  const handleTextChange = (e) => {
    const value = e.target.value;
    setNewNote(value);

    // Check for @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setShowUserSuggestions(true);
        setSuggestionIndex(lastAtIndex);
      } else {
        setShowUserSuggestions(false);
      }
    } else {
      setShowUserSuggestions(false);
    }
  };

  // Handle user selection from suggestions
  const handleUserSelect = (user) => {
    const cursorPos = textareaRef.current.selectionStart;
    const textBeforeCursor = newNote.substring(0, suggestionIndex);
    const textAfterCursor = newNote.substring(cursorPos);
    
    const newText = textBeforeCursor + `@${user.email} ` + textAfterCursor;
    setNewNote(newText);
    setShowUserSuggestions(false);
    
    // Add to tagged users if not already there
    if (!taggedUsers.includes(user.email)) {
      setTaggedUsers([...taggedUsers, user.email]);
    }
    
    // Focus back to textarea
    setTimeout(() => {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(newText.length, newText.length);
    }, 0);
  };

  // Handle note submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !currentUser) return;

    setIsSubmitting(true);
    try {
      // Extract tagged users from the note content
      const mentionRegex = /@(\S+)/g;
      const mentions = [...newNote.matchAll(mentionRegex)].map(match => match[1]);
      const allTaggedUsers = [...new Set([...taggedUsers, ...mentions])];

      // Add the note
      await addNoteToPost(
        postId, 
        currentUser.uid, 
        currentUser.email, 
        newNote.trim(),
        allTaggedUsers
      );

      // Create notifications for tagged users
      if (allTaggedUsers.length > 0) {
        await createNotifications(
          postId,
          currentUser.email,
          allTaggedUsers,
          newNote.trim()
        );
      }

      // Refresh notes
      const updatedNotes = await getPostNotes(postId);
      setNotes(updatedNotes);
      
      // Clear form
      setNewNote('');
      setTaggedUsers([]);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle note edit
  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  // Handle note update
  const handleUpdateNote = async (noteId) => {
    if (!editingContent.trim()) return;

    try {
      // Extract tagged users from the note content
      const mentionRegex = /@(\S+)/g;
      const mentions = [...editingContent.matchAll(mentionRegex)].map(match => match[1]);
      
      await updateNote(postId, noteId, editingContent.trim(), mentions);
      
      // Refresh notes
      const updatedNotes = await getPostNotes(postId);
      setNotes(updatedNotes);
      
      // Clear editing state
      setEditingNoteId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note. Please try again.');
    }
  };

  // Handle note deletion
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteNote(postId, noteId);
      
      // Remove from local state
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Filter users for suggestions
  const filteredUsers = allUsers.filter(user => 
    user.email && 
    user.email !== currentUser?.email &&
    user.email.toLowerCase().includes(
      newNote.substring(suggestionIndex + 1).toLowerCase()
    )
  );

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading notes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Notes List */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            <User className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>No notes yet. Be the first to leave feedback!</p>
          </div>
        ) : (
          notes.map((note) => {
            const isOwnNote = note.userId === currentUser?.uid || note.userEmail === currentUser?.email;
            const isEditing = editingNoteId === note.id;
            
            return (
              <div key={note.id} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                      {note.userEmail?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {note.userEmail}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatTime(note.createdAt)}
                    </span>
                  </div>
                  
                  {/* Edit/Delete buttons - only show for own notes */}
                  {isOwnNote && !isEditing && (
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEditNote(note)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit note"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete note"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                      rows={3}
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingContent('');
                        }}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateNote(note.id)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {note.content}
                    </div>
                    
                    {note.taggedUsers && note.taggedUsers.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {note.taggedUsers.map((email, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                          >
                            <At className="w-3 h-3 mr-1" />
                            {email}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Note Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newNote}
            onChange={handleTextChange}
            placeholder="Leave a note... Use @username to tag someone"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
            disabled={isSubmitting}
          />
          
          {/* User Suggestions */}
          {showUserSuggestions && filteredUsers.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto z-10">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleUserSelect(user)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                >
                  <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-xs font-medium">
                    {user.email?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm">{user.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tagged Users Display */}
        {taggedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <span className="text-xs text-gray-500">Tagged:</span>
            {taggedUsers.map((email, index) => (
              <span 
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
              >
                <At className="w-3 h-3 mr-1" />
                {email}
              </span>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            disabled={isSubmitting}
          >
            Close
          </button>
          <button
            type="submit"
            disabled={!newNote.trim() || isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm"
          >
            <PaperPlaneTilt className="w-4 h-4" />
            <span>{isSubmitting ? 'Posting...' : 'Post Note'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
